<template>
  <div class="mb-1">
    <div v-if="b2b" class="flex justify-between items-center pb-1">
      <span class="font-semibold" :qaid="`product-${label}-label`">{{ label }}</span>
      <size-link v-if="isSizeLinkEnabled" :size-link-guide="product.sizeLinkGuide" />
    </div>
    <div v-if="isSizeLinkEnabled && !b2b" class="flex w-full justify-end pb-1">
      <size-link :size-link-guide="product.sizeLinkGuide" />
    </div>
    <div v-if="items.length" ref="grid" qaid="pdp-product-size-attribute-grid">
      <pdl-form-group
        v-model="selectedAttributes[code]"
        :input-group-classes="gridClasses"
        variant="radioButton"
        type="radio"
        :name="`${code}-selector`"
        :multi-items="items"
        :label="label"
        :show-label="false"
        @update:modelValue="handleAttributeUpdate"
      />
    </div>
  </div>
</template>
<script>
import {Attribute} from '@/components/containers/buy-zone/attributes/mixins/attribute';
import {PdlFormGroup} from '@pedal/pdl-form-group';
import SizeLink from '@/components/containers/buy-zone/attributes/SizeLink';
import {mapState} from 'vuex';
import {isInStock} from '@/components/containers/pdp/utils/retailer-helpers';
import {getSelectedVariant} from '@/utils/variant-helpers.js';
import debounce from 'lodash/debounce';

const templateGridCols = {
  1: `grid-cols-1`,
  2: `grid-cols-2`,
  3: `grid-cols-3`,
  4: `grid-cols-4`,
  5: `grid-cols-5`,
  6: `grid-cols-6`,
  7: `grid-cols-7`,
  8: `grid-cols-8`,
  9: `grid-cols-9`,
  10: `grid-cols-10`,
  11: `grid-cols-11`,
  12: `grid-cols-12`,
};

export default {
  name: 'ProductAttributeButtonGrid',
  components: {PdlFormGroup, SizeLink},
  mixins: [Attribute],
  props: {
    gap: {
      type: Number,
      default: 8,
    },
  },

  data() {
    return {
      isCalculated: false,
      columns: 0,
      maxWidth: 0,
      resizeTimeout: null,
    };
  },
  computed: {
    ...mapState('backend', ['b2b', 'isB2Bsite']),
    ...mapState('productDetails', ['isECommAvailable', 'sizeChartData']),

    items() {
      let items = [];

      this.attribute.forEach((item) => {
        const selected = {
          ...this.selectedAttributes,
          [this.code]: item.value,
        };

        const variant = getSelectedVariant({product: this.product, selectedAttributes: selected});

        if (this.isB2Bsite && !variant) return;

        items.push({
          ...item,
          checked: item.value === this.selectedAttributes[item.code],
          isUnavailable: !this.isAvailable(item),
        });
      });

      return items;
    },
    gridClasses() {
      return [this.isCalculated ? 'grid' : 'block', templateGridCols[this.columns], `gap-2`].join(' ');
    },
    hasSizeAttribute() {
      return this.attribute.some((attribute) => attribute.code?.toLocaleLowerCase().includes('size'));
    },
    isSizeLinkEnabled() {
      return !!(this.hasSizeAttribute && (this.product?.sizeLinkGuide || this.sizeChartData));
    },
  },
  watch: {
    items: {
      deep: true,
      immediate: true,
      handler() {
        this.recalculateSizes();
      },
    },
  },
  mounted() {
    this.observeGridSize();
  },
  updated() {
    this.observeGridSize();
  },
  unmounted() {
    this.gridObserver && this.gridObserver.disconnect();
  },
  methods: {
    recalculateSizes: debounce(function () {
      this.isCalculated = false;
      this.$nextTick(() => {
        this.columns = 0;
        this.maxWidth = 0;
        this.calculateColumns();
      });
    }, 300),
    observeGridSize() {
      if (!this.$refs.grid || this.gridObserver) {
        return;
      }
      this.gridObserver = new ResizeObserver(this.calculateColumns);
      this.gridObserver.observe(this.$refs.grid);
      this.calculateColumns();
    },
    initSizes() {
      if (this.isCalculated) {
        return;
      }
      const spans = this.$refs.grid?.querySelectorAll('.radio-button');
      let widths = Array.from(spans).map((b) => b.offsetWidth);
      this.maxWidth = Math.max(...widths) + this.gap;
    },
    calculateColumns() {
      this.initSizes();
      let containerWidth = this.$refs.grid?.clientWidth;
      this.columns = Math.floor(containerWidth / this.maxWidth);
      if (this.columns) {
        //We have a valid calculation so set iscalculated so the widths/maxwidths don't get recalculated
        this.isCalculated = true;
      }
    },
    isAvailable(item) {
      if (!this.isECommAvailable) return true;
      const variantAttributeCodes = this.product?.variantAttributesDirectory?.map((attr) => attr.code) || [];
      // Filter selectedAttributes to only include keys present in variantAttributeCodes
      const filteredSelectedAttributes = Object.fromEntries(
        Object.entries(this.selectedAttributes).filter(([key]) => variantAttributeCodes.includes(key))
      );

      const variant = this.product.variants.find((v) => {
        return Object.entries(filteredSelectedAttributes).every(([key, value]) => {
          if (key === item.code) {
            return v.variantAttributes[item.code] === item.value;
          }
          return v.variantAttributes[key] === value;
        });
      });

      return variant?.isLocallyStockAvailable || isInStock(this.b2b, variant?.globalStockStatus);
    },
    handleAttributeUpdate(value) {
      this.updateSelectedAttributes({[this.code]: value});
    },
  },
};
</script>
<style lang="scss" scoped>
:deep(.form-fieldset) {
  margin: 0;
}
</style>
